import React, { useState, useEffect, useRef } from "react";
import Table from "@/common/elements/Table";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../../InmateDetails/styles";
import Button from "@/common/elements/Button";
import { Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import {
  getFullAddress,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import useDialog from "@/hooks/useDialog";
import CustomModal from "../../CustomModal";
import AddressesForm from "./AddressesForm";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const ADDRESSES_HEADERS = [
  { key: "type", text: "Type" },
  // Starting width so the column doesn't grow unbounded (and trigger the
  // table's horizontal scrollbar) on first render — colWidths seeds from
  // this, and the live Config/resize drag overrides it dynamically from
  // there (see common/elements/Table's ellipsis handling for "fullAddress").
  { key: "fullAddress", text: "Full Address", width: 600 },
];

export const AddressesList = ({
  selectedAddresses,
  associates,
  onAddressesChange,
  hideActions = false,
  fullWidth = false,
  startingPoint="",
  // Optional (permission) => boolean override for callers outside Bookings
  // (e.g. Civil Papers) that must check their own module's rights instead
  // of Bookings' Arrest Report sub-module.
  securityCheck = null,
}) => {
  const checkSecurity = (permission) =>
    securityCheck
      ? securityCheck(permission)
      : hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          permission,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report
        );
  const { showDialog } = useDialog();
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state.bookings
  );
  const addressFormRef = useRef();

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This component is shared
  // across modules (Bookings' Personal Information tab, Staff's Personal
  // Information tab via CreateStaffing, etc.) — gridId must carry the
  // caller's module context (via startingPoint) rather than being hardcoded
  // to "Bookings", or two different screens end up reading/writing the same
  // saved column layout.
  const {
    headers: addressHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    `${startingPoint || "Bookings"}:BioAddresses`,
    ADDRESSES_HEADERS
  );

  const [showEdit, setShowEdit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectAddress, setSelectAddress] = useState(null);
  const [addressData, setAddressData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isDiasbled, setIsDisabled] = useState(false);
  // Use the selectedAddresses prop if available; otherwise, use Redux value.
  useEffect(() => {
    let addresses;
    if (associates) {
      // When associates is true, use selectedAddresses (even if null, default to empty array)
      addresses = selectedAddresses || [];
    } else {
      addresses = bookings?.bio?.[0]?.bioAddresses || [];
    }
    // Process addresses only if they exist
    addresses = addresses.map((a) => ({
      ...a,
      type: a?.addresses?.[0]?.addressType || "",
      fullAddress: getFullAddress(a?.addresses?.[0]),
      uuid: a.uuid || uuidv4(),
    }));
    setAddressData(addresses);
  }, [selectedAddresses, bookings, associates]);

  const openModal = () => {
    setShowModal(true);
  };

  const handleEditAddress = () => {
    setIsEdit(true);
    openModal();
  };

  const handleAddAddress = () => {
    setSelectAddress(null);
    setIsEdit(false);
    openModal();
  };

  const selectAddressDataFn = (value) => {
    setSelectAddress({
      ...value?.addresses?.[0],
      uuid: value?.uuid,
    });
    setShowEdit(true);
  };

  const handleSave = () => {
    addressFormRef?.current?.validateFields().then((values) => {
      // Split AddressesExtra dynamic fields out of the form values — nested
      // onto addresses[0].addressesExtra[0] below rather than left flat on
      // values, where they'd otherwise leak into the main bookings save
      // payload (non-associates mode only — see pages/Bookings/index.jsx's
      // strip-before-POST and saveBioAddressesExtra).
      const addressesExtraFieldKeys =
        addressFormRef?.current?.addressesExtraFieldKeys || [];
      const addressesExtraTypeMap =
        addressFormRef?.current?.addressesExtraTypeMap || {};
      const addressesExtraData = {};
      addressesExtraFieldKeys.forEach((k) => {
        const val = values?.[k];
        const htmlType = addressesExtraTypeMap[k];
        addressesExtraData[k] = val
          ? htmlType === "date"
            ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime"
              ? dayjs(val).format("MM/DD/YYYY HH:mm")
              : val
          : null;
        delete values[k];
      });

      let ad = [...addressData];
      if (isEdit) {
        ad = ad.map((a) => {
          if (a?.uuid === selectAddress.uuid) {
            const existingAddress = (a.addresses && a.addresses[0]) || {};
            const existingExtra = existingAddress?.addressesExtra?.[0] || {};
            return {
              ...a,
              addresses: [
                {
                  ...existingAddress,
                  ...values,
                  addressesExtra:
                    addressesExtraFieldKeys.length > 0
                      ? [
                          {
                            ...existingExtra,
                            ...addressesExtraData,
                            addressesExtraID:
                              existingExtra?.addressesExtraID ??
                              addressFormRef?.current?.addressesExtraID ??
                              0,
                            addressID:
                              existingExtra?.addressID ??
                              existingAddress?.addressId ??
                              0,
                          },
                        ]
                      : existingAddress?.addressesExtra,
                },
              ],
            };
          }
          return a;
        });
      } else {
        const data = {
          addresses: [
            {
              ...values,
              addressesExtra:
                addressesExtraFieldKeys.length > 0
                  ? [
                      {
                        ...addressesExtraData,
                        addressesExtraID: 0,
                        addressID: 0,
                      },
                    ]
                  : undefined,
            },
          ],
          uuid: uuidv4(),
        };
        ad.push(data);
      }

      if (associates) {
        // In associates mode, update parent through onAddressesChange (done in useEffect)
        setAddressData(ad);
        if (typeof onAddressesChange === "function") {
          onAddressesChange(ad);
        }
      } else {
        dispatch(
          setBookings({
            bio: [
              {
                ...bookings?.bio?.[0],
                bioAddresses: ad,
              },
            ],
          })
        );
      }
      handleCancel();
    });
  };

  const preDelete = () => {
    showDialog("confirm", {
      title: "Are you sure you want to delete this address?",
      onConfirm: () => handleDelete(),
    });
  };

  const handleDelete = () => {
    const selAddressData = addressData.find(
      (p) => p?.uuid === selectAddress?.uuid
    );
    const updatedAddressData = addressData.filter(
      (p) => p?.uuid !== selectAddress?.uuid
    );
    setAddressData(updatedAddressData);

    if (!associates) {
      dispatch(
        setBookings({
          bio: [
            {
              ...bookings?.bio?.[0],
              bioAddresses: updatedAddressData,
            },
          ],
        })
      );
    }

    if (isNonEmptyVariable(selAddressData?.bioAddressId)) {
      const url =
        API_ENDPOINTS.deleteBioAddresses + "/" + selAddressData?.bioAddressId;
      dispatch(setDeleteAPis(url));
    }

    if (isNonEmptyVariable(selAddressData?.addressId)) {
      const url = API_ENDPOINTS.addresses + "/id/" + selAddressData?.addressId;
      dispatch(setDeleteAPis(url));
    }

    handleCancel();
  };

  const handleCancel = () => {
    setShowModal(false);
    setShowEdit(false);
    setSelectAddress(null);
    setIsEdit(false);
  };
  useEffect(() => {
    if (showEdit) {
      let showSave = checkSecurity(LABELS_CAPITAL.MODIFY);
      if (showSave) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [showEdit]);
  return (
    <>
      <InmateDetailsTableContainer
        style={{ width: fullWidth ? "100%" : "auto" }}
      >
        <TableHeader>
          <h6>List of address</h6>
          {!hideActions && (
            <div className="right">
              {
                <Button
                  onClick={handleAddAddress}
                  disabled={
                    !bookingsFieldsEnabled ||
                    isConfiguring ||
                    !checkSecurity(LABELS_CAPITAL.ADD)
                  }
                >
                  Add Address
                </Button>
              }
              {
                <Button
                  onClick={handleEditAddress}
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !showEdit ||
                    isConfiguring ||
                    !checkSecurity(LABELS_CAPITAL.VIEW)
                  }
                >
                  Details
                </Button>
              }
              {
                <Button
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !showEdit ||
                    !bookingsFieldsEnabled ||
                    isConfiguring ||
                    !checkSecurity(LABELS_CAPITAL.DELETE)
                  }
                  onClick={preDelete}
                >
                  Delete
                </Button>
              }
              <Button
                onClick={handleCancel}
                style={{ marginLeft: "10px" }}
                disabled={!showEdit || isConfiguring}
              >
                Cancel
              </Button>

              {/* Config — drag-reorder/resize columns; only while Modify is active */}
              {!isConfiguring ? (
                <Button
                  htmlType="button"
                  onClick={startConfiguring}
                  style={{ marginLeft: "10px" }}
                >
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ disabled: false }}
                  cancelButtonProps={{ disabled: false }}
                >
                  <Button
                    htmlType="button"
                    style={{ marginLeft: "10px" }}
                  >
                    {configButtonLabel}
                  </Button>
                </Popconfirm>
              )}
            </div> )
          }
        </TableHeader>

        {checkSecurity(LABELS_CAPITAL.VIEW) && (
          <Table
            ref={dataTableRef}
            headers={addressHeaders}
            draggable={isConfiguring}
            data={addressData}
            onRowClick={selectAddressDataFn}
            onRowDoubleClick={(value) => {
              console.log("OPEN MODAL FROM DOUBLE CLICK");
              selectAddressDataFn(value);
              handleEditAddress();
            }}
            selected={selectAddress === null}
          />
        )}
      </InmateDetailsTableContainer>

      <CustomModal
        header="Address Information"
        designPattern="ant"
        show={showModal}
        onHide={handleCancel}
        onSaveClick={handleSave}
        onCloseClick={handleCancel}
        submitButtonDisabled={!bookingsFieldsEnabled}
        isDisabled={!isDiasbled && !bookingsFieldsEnabled}
        width={"50%"}
        size="lg"
      >
        {showModal && (
          <AddressesForm 
            formData={selectAddress || {}} 
            ref={addressFormRef} 
            bookingsFieldsEnabled={bookingsFieldsEnabled}
            startingPoint={startingPoint}
          />
        )}
      </CustomModal>
    </>
  );
};

export default AddressesList;
